import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TextInput,
  Textarea,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  Badge,
} from "flowbite-react";
import { HiPlus, HiPencil, HiTrash, HiUsers, HiRefresh } from "react-icons/hi";
import {
  getAllTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeamWithWorkers,
  type TeamWithWorkers,
} from "../../services/teams";
import type { Database } from "../../pages/types/supabase";

type Team = Database["public"]["Tables"]["teams"]["Row"];

interface TeamFormData {
  name: string;
  description: string;
}

interface TeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TeamManagementModal: React.FC<TeamManagementModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [teams, setTeams] = useState<TeamWithWorkers[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<TeamFormData>({
    name: "",
    description: "",
  });
  const [formLoading, setFormLoading] = useState(false);

  // Load teams when modal opens and lock body scroll
  useEffect(() => {
    if (!isOpen) return;

    void loadTeams();

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const teamsData = await getAllTeams();
      
      // Get detailed team data with workers
      const teamsWithWorkers = await Promise.all(
        teamsData.map(async (team) => {
          try {
            const teamWithWorkers = await getTeamWithWorkers(team.id);
            return teamWithWorkers!;
          } catch (err) {
            console.warn(`Failed to load workers for team ${team.id}:`, err);
            return {
              ...team,
              workers: [],
            };
          }
        })
      );
      
      setTeams(teamsWithWorkers);
    } catch (err) {
      console.error("Error loading teams:", err);
      setError(err instanceof Error ? err.message : "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({ name: "", description: "" });
    setShowAddForm(false);
    setEditingTeam(null);
    setFormLoading(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddTeam = () => {
    setShowAddForm(true);
    setEditingTeam(null);
    setFormData({ name: "", description: "" });
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    setShowAddForm(true);
    setFormData({
      name: team.name,
      description: team.description || "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError("Team name is required");
      return;
    }

    try {
      setFormLoading(true);
      setError(null);
      setSuccess(null);

      if (editingTeam) {
        // Update existing team
        await updateTeam(editingTeam.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        });
        setSuccess("Team successfully updated!");
      } else {
        // Create new team
        await createTeam({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          active: true,
        });
        setSuccess("Team successfully created!");
      }

      // Reload teams and reset form
      await loadTeams();
      resetForm();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error saving team:", err);
      setError(err instanceof Error ? err.message : "Failed to save team");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTeam = async (team: Team) => {
    const teamWithWorkers = teams.find(t => t.id === team.id);
    const memberCount = teamWithWorkers?.workers.length || 0;
    
    let confirmMessage = `Are you sure you want to delete the team "${team.name}"?`;
    if (memberCount > 0) {
      confirmMessage += `\n\nThis team has ${memberCount} member${memberCount !== 1 ? 's' : ''}. They will be removed from this team but not deleted from the system.`;
    }
    confirmMessage += "\n\nThis action cannot be undone.";
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      await deleteTeam(team.id);
      setSuccess(`Team "${team.name}" successfully deleted!`);
      
      // Reload teams
      await loadTeams();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error deleting team:", err);
      setError(err instanceof Error ? err.message : "Failed to delete team");
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      size="7xl"
      className="team-management-modal"
    >
      <div className="flex max-h-[85vh] w-full flex-col">
        <ModalHeader className="border-b border-gray-200 !p-6 dark:border-gray-700">
          <strong>Team Management</strong>
        </ModalHeader>
        
        <ModalBody className="flex-1 overflow-y-auto !p-6">
          {/* Alert Messages */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-3 text-green-800">
              {success}
            </div>
          )}

          {/* Action Buttons */}
          {!showAddForm && (
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button color="primary" onClick={handleAddTeam}>
                <div className="flex items-center gap-x-2">
                  <HiPlus className="text-lg" />
                  Add New Team
                </div>
              </Button>
              
              <Button color="light" onClick={loadTeams} disabled={loading}>
                <div className="flex items-center gap-x-2">
                  <HiRefresh className={`text-lg ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </div>
              </Button>
            </div>
          )}

          {/* Add/Edit Team Form */}
          {showAddForm && (
            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {editingTeam ? "Edit Team" : "Add New Team"}
              </h3>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Team Name *
                  </label>
                  <TextInput
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter team name"
                    required
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Description
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter team description (optional)"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="mt-4 flex gap-2">
                <Button
                  color="primary"
                  onClick={handleSubmit}
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      Saving...
                    </div>
                  ) : (
                    editingTeam ? "Update Team" : "Create Team"
                  )}
                </Button>
                <Button color="gray" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Teams Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
                <span className="ml-2">Loading teams...</span>
              </div>
            ) : teams.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                <HiUsers className="mx-auto mb-2 text-4xl" />
                <p className="text-lg font-medium">No teams found</p>
                <p className="text-sm">Create your first team to get started.</p>
              </div>
            ) : (
              <div className="hidden sm:block">
                <Table hoverable>
                  <TableHead>
                    <TableHeadCell>Team Name</TableHeadCell>
                    <TableHeadCell>Description</TableHeadCell>
                    <TableHeadCell>Members</TableHeadCell>
                    <TableHeadCell>Created</TableHeadCell>
                    <TableHeadCell>Actions</TableHeadCell>
                  </TableHead>
                  <TableBody className="divide-y">
                    {teams.map((team) => (
                      <TableRow
                        key={team.id}
                        className="bg-white dark:border-gray-700 dark:bg-gray-800"
                      >
                        <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            <HiUsers className="text-gray-400" />
                            {team.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate">
                            {team.description || (
                              <span className="text-gray-400 italic">No description</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge color="info" size="sm">
                            {team.workers.length} member{team.workers.length !== 1 ? 's' : ''}
                          </Badge>
                          {team.workers.length > 0 && (
                            <div className="mt-1 text-xs text-gray-500">
                              {team.workers
                                .slice(0, 3)
                                .map((worker) => worker.full_name)
                                .join(", ")}
                              {team.workers.length > 3 && ` +${team.workers.length - 3} more`}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(team.created_at).toLocaleDateString("de-DE")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="xs"
                              color="gray"
                              onClick={() => handleEditTeam(team)}
                              title="Edit team"
                            >
                              <HiPencil className="text-sm" />
                            </Button>
                            <Button
                              size="xs"
                              color="failure"
                              onClick={() => handleDeleteTeam(team)}
                              title="Delete team"
                            >
                              <HiTrash className="text-sm" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Mobile Card View */}
            {!loading && teams.length > 0 && (
              <div className="space-y-4 sm:hidden">
                {teams.map((team) => (
                  <div
                    key={team.id}
                    className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <HiUsers className="text-gray-400" />
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {team.name}
                        </h3>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => handleEditTeam(team)}
                          title="Edit team"
                        >
                          <HiPencil className="text-sm" />
                        </Button>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => handleDeleteTeam(team)}
                          title="Delete team"
                        >
                          <HiTrash className="text-sm" />
                        </Button>
                      </div>
                    </div>
                    
                    {team.description && (
                      <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                        {team.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <Badge color="info" size="sm">
                        {team.workers.length} member{team.workers.length !== 1 ? 's' : ''}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(team.created_at).toLocaleDateString("de-DE")}
                      </span>
                    </div>
                    
                    {team.workers.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <strong>Members:</strong>{" "}
                        {team.workers
                          .slice(0, 3)
                          .map((worker) => worker.full_name)
                          .join(", ")}
                        {team.workers.length > 3 && ` +${team.workers.length - 3} more`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalBody>
        
        <ModalFooter className="shrink-0 justify-end">
          <Button color="gray" onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default TeamManagementModal;
